import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useParallax } from "../../hooks/useScrollAnimation";
import { TextRevealByWord } from "../animations/TextReveal";
import MagneticButton from "../animations/MagneticButton";
import BlobBackground from "../animations/BlobBackground";

const HeroSection = () => {
  const parallaxOffset = useParallax(0.3);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.2,
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  const floatingVariants = {
    animate: {
      y: [-10, 10, -10],
      rotate: [0, 5, 0, -5, 0],
      transition: {
        duration: 6,
        repeat: Infinity,
        ease: "easeInOut",
      },
    },
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-primary-50 via-white to-accent-50 liquid-section liquid-padding-x liquid-padding-y" style={{ paddingTop: 'clamp(2rem, 4vw, 6rem)' }}>
      {/* Animated blob background */}
      <BlobBackground />

      <div className="liquid-container liquid-container-lg relative z-10 liquid-container-query">
        <div className="liquid-grid lg:grid-cols-2 liquid-gap items-center">
          {/* Left column - Text content */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="text-center lg:text-left"
          >
            <motion.div variants={itemVariants} className="inline-block mb-4">
              <span className="inline-flex items-center liquid-padding-sm rounded-full glass-effect liquid-text-sm font-medium text-primary-700 shadow-sm liquid-transition">
                <span className="w-2 h-2 bg-primary-500 rounded-full mr-2 animate-pulse" />
                Building a Smarter Future with AI-in-the-Loop
              </span>
            </motion.div>

            <motion.h1
              variants={itemVariants}
              className="liquid-hero font-bold text-gray-900 mb-6 leading-tight liquid-transition"
            >
              <TextRevealByWord
                text="AI First. Human Always."
                className="inline-block"
              />
              <span className="block text-gradient-vibrant">
                <TextRevealByWord
                  text="Intelligence Operations"
                  delay={0.4}
                  className="inline-block"
                />
              </span>
            </motion.h1>

            <motion.p
              variants={itemVariants}
              className="liquid-text-xl text-gray-600 mb-8 leading-relaxed liquid-transition"
            >
              We help SMEs and startups deploy AI that works safely, accurately, and alongside people — not instead of them.
              Combine Artificial Intelligence with human oversight to create a new standard for intelligent operations —
              scalable, compliant, and designed for real business impact.
            </motion.p>

            <motion.div
              variants={itemVariants}
              className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start flex-wrap"
            >
              <Link to="/templates">
                <MagneticButton
                  className="liquid-button bg-gradient-to-r from-primary-600 to-primary-700 text-white font-semibold shadow-lg shadow-primary-500/30 hover:shadow-xl hover:shadow-primary-500/40 liquid-transition liquid-ease-apple"
                  strength={0.3}
                >
                  Hire a Preconfigured Agent
                </MagneticButton>
              </Link>
              <Link to="/register">
                <MagneticButton
                  className="liquid-button glass-effect text-gray-900 font-semibold hover:bg-white/90 liquid-transition liquid-ease-apple text-center"
                  strength={0.25}
                >
                  Build Your AI Agent
                </MagneticButton>
              </Link>
              <Link to="/contact">
                <MagneticButton
                  className="liquid-button border-2 border-primary-600 text-primary-700 font-semibold hover:bg-primary-50 liquid-transition liquid-ease-apple text-center"
                  strength={0.25}
                >
                  Request Industry Solution
                </MagneticButton>
              </Link>
            </motion.div>

            <motion.div
              variants={itemVariants}
              className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12"
            >
              <div className="text-center lg:text-left">
                <p className="text-2xl font-bold text-primary-700">50+</p>
                <p className="text-sm text-gray-600">AI Agents Deployed</p>
              </div>
              <div className="text-center lg:text-left">
                <p className="text-2xl font-bold text-primary-700">2×</p>
                <p className="text-sm text-gray-600">Faster Operations</p>
              </div>
              <div className="text-center lg:text-left">
                <p className="text-2xl font-bold text-primary-700">40%</p>
                <p className="text-sm text-gray-600">Cost Reduction</p>
              </div>
              <div className="text-center lg:text-left">
                <p className="text-2xl font-bold text-primary-700">Built-in</p>
                <p className="text-sm text-gray-600">AI Guardrails</p>
              </div>
            </motion.div>
          </motion.div>

          {/* Right column - AI Employee Cards */}
          <div className="relative hidden lg:block" style={{ height: 'clamp(500px, 40vw, 600px)' }}>
            {/* Alfred - AI Executive Assistant */}
            <motion.div
              variants={floatingVariants}
              animate="animate"
              className="absolute top-0 right-0 liquid-card border border-purple-200 liquid-smooth"
              style={{ 
                transform: `translateY(${parallaxOffset * 0.5}px)`,
                width: 'clamp(280px, 20vw, 320px)',
                height: 'clamp(200px, 15vw, 224px)'
              }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-16 h-16 rounded-xl overflow-hidden">
                  <img
                    src="/img/alex.png"
                    alt="Alex - AI Executive Assistant"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <h3 className="font-bold text-purple-700 text-lg">Alex</h3>
                  <p className="text-sm text-gray-600">
                    AI Executive Assistant
                  </p>
                </div>
              </div>
              <div className="space-y-2">
                <div className="text-sm font-medium text-gray-700 mb-2">
                  What I can do:
                </div>
                <div className="flex flex-wrap gap-2">
                  {[
                    "Schedule Calendar",
                    "Prioritize Tasks",
                    "Reply to Emails",
                    "Takes Notes",
                  ].map((skill, i) => (
                    <span
                      key={i}
                      className="px-3 py-1 bg-purple-100 text-purple-700 text-xs rounded-full"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  and hundreds more skills...
                </p>
              </div>
            </motion.div>

            {/* Chip - AI Sales Representative */}
            <motion.div
              variants={floatingVariants}
              animate="animate"
              className="absolute top-32 left-0 liquid-card border border-blue-200 liquid-smooth"
              style={{ 
                transform: `translateY(${parallaxOffset * 0.8}px)`,
                width: 'clamp(260px, 18vw, 288px)',
                height: 'clamp(200px, 15vw, 224px)'
              }}
              transition={{ delay: 0.2 }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-16 h-16 rounded-xl overflow-hidden">
                  <img
                    src="/img/max.png"
                    alt="Max - AI Sales Representative"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <h3 className="font-bold text-blue-700 text-lg">Max</h3>
                  <p className="text-sm text-gray-600">
                    AI Sales Representative
                  </p>
                </div>
              </div>
              <div className="space-y-2">
                <div className="text-sm font-medium text-gray-700 mb-2">
                  What I can do:
                </div>
                <div className="flex flex-wrap gap-2">
                  {[
                    "Update CRM",
                    "Chase Opportunities",
                    "Find Leads",
                    "Personalized Outreach",
                  ].map((skill, i) => (
                    <span
                      key={i}
                      className="px-3 py-1 bg-blue-100 text-blue-700 text-xs rounded-full"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  and hundreds more skills...
                </p>
              </div>
            </motion.div>

            {/* Clide - AI Customer Support Specialist */}
            <motion.div
              variants={floatingVariants}
              animate="animate"
              className="absolute bottom-24 right-12 liquid-card border border-red-200 liquid-smooth"
              style={{ 
                transform: `translateY(${parallaxOffset * 0.6}px)`,
                width: 'clamp(240px, 16vw, 256px)',
                height: 'clamp(200px, 15vw, 224px)'
              }}
              transition={{ delay: 0.4 }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-16 h-16 rounded-xl overflow-hidden">
                  <img
                    src="/img/luna.png"
                    alt="Luna - AI Customer Support Specialist"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <h3 className="font-bold text-red-700 text-lg">Luna</h3>
                  <p className="text-sm text-gray-600">AI Customer Support</p>
                </div>
              </div>
              <div className="space-y-2">
                <div className="text-sm font-medium text-gray-700 mb-2">
                  What I can do:
                </div>
                <div className="flex flex-wrap gap-2">
                  {[
                    "Respond to Tickets",
                    "Escalate Issues",
                    "Take Actions",
                    "Solve Problems",
                  ].map((skill, i) => (
                    <span
                      key={i}
                      className="px-3 py-1 bg-red-100 text-red-700 text-xs rounded-full"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  and hundreds more skills...
                </p>
              </div>
            </motion.div>

            {/* Suki - AI Marketing Associate */}
            <motion.div
              variants={floatingVariants}
              animate="animate"
              className="absolute bottom-0 left-8 liquid-card border border-yellow-200 liquid-smooth"
              style={{ 
                transform: `translateY(${parallaxOffset * 0.3}px)`,
                width: 'clamp(260px, 18vw, 288px)',
                height: 'clamp(180px, 13vw, 192px)'
              }}
              transition={{ delay: 0.6 }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-16 h-16 rounded-xl overflow-hidden">
                  <img
                    src="/img/nova.png"
                    alt="Nova - AI Marketing Associate"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <h3 className="font-bold text-yellow-700 text-lg">Nova</h3>
                  <p className="text-sm text-gray-600">
                    AI Marketing Associate
                  </p>
                </div>
              </div>
              <div className="space-y-2">
                <div className="text-sm font-medium text-gray-700 mb-2">
                  What I can do:
                </div>
                <div className="flex flex-wrap gap-2">
                  {[
                    "Write Blog Posts",
                    "Run SEO",
                    "Manage Social Media",
                    "Write with Brand Voice",
                  ].map((skill, i) => (
                    <span
                      key={i}
                      className="px-3 py-1 bg-yellow-100 text-yellow-700 text-xs rounded-full"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  and hundreds more skills...
                </p>
              </div>
            </motion.div>
          </div>

          {/* Mobile AI Agent Cards */}
          <div className="lg:hidden mt-12">
            <div className="grid grid-cols-2 gap-4">
              {/* Alex - AI Executive Assistant */}
              <motion.div
                variants={itemVariants}
                className="glass-effect rounded-2xl shadow-lg p-4 border border-purple-200"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-xl overflow-hidden">
                    <img
                      src="/img/alex.png"
                      alt="Alex - AI Executive Assistant"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <h3 className="font-bold text-purple-700 text-sm">Alex</h3>
                    <p className="text-xs text-gray-600">Executive Assistant</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="text-xs font-medium text-gray-700 mb-1">
                    What I can do:
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {["Schedule", "Tasks", "Emails", "Notes"].map(
                      (skill, i) => (
                        <span
                          key={i}
                          className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full"
                        >
                          {skill}
                        </span>
                      )
                    )}
                  </div>
                </div>
              </motion.div>

              {/* Max - AI Sales Representative */}
              <motion.div
                variants={itemVariants}
                className="glass-effect rounded-2xl shadow-lg p-4 border border-blue-200"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-xl overflow-hidden">
                    <img
                      src="/img/max.png"
                      alt="Max - AI Sales Representative"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <h3 className="font-bold text-blue-700 text-sm">Max</h3>
                    <p className="text-xs text-gray-600">Sales Rep</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="text-xs font-medium text-gray-700 mb-1">
                    What I can do:
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {["CRM", "Leads", "Outreach", "Follow-up"].map(
                      (skill, i) => (
                        <span
                          key={i}
                          className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full"
                        >
                          {skill}
                        </span>
                      )
                    )}
                  </div>
                </div>
              </motion.div>

              {/* Luna - AI Customer Support */}
              <motion.div
                variants={itemVariants}
                className="glass-effect rounded-2xl shadow-lg p-4 border border-red-200"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-xl overflow-hidden">
                    <img
                      src="/img/luna.png"
                      alt="Luna - AI Customer Support"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <h3 className="font-bold text-red-700 text-sm">Luna</h3>
                    <p className="text-xs text-gray-600">Support</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="text-xs font-medium text-gray-700 mb-1">
                    What I can do:
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {["Tickets", "Issues", "Actions", "Solutions"].map(
                      (skill, i) => (
                        <span
                          key={i}
                          className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full"
                        >
                          {skill}
                        </span>
                      )
                    )}
                  </div>
                </div>
              </motion.div>

              {/* Nova - AI Marketing Associate */}
              <motion.div
                variants={itemVariants}
                className="glass-effect rounded-2xl shadow-lg p-4 border border-yellow-200"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-xl overflow-hidden">
                    <img
                      src="/img/nova.png"
                      alt="Nova - AI Marketing Associate"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <h3 className="font-bold text-yellow-700 text-sm">Nova</h3>
                    <p className="text-xs text-gray-600">Marketing</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="text-xs font-medium text-gray-700 mb-1">
                    What I can do:
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {["Blogs", "SEO", "Social", "Brand"].map((skill, i) => (
                      <span
                        key={i}
                        className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded-full"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 1.5, repeat: Infinity }}
      >
        <div className="w-6 h-10 border-2 border-gray-400 rounded-full flex justify-center">
          <motion.div
            className="w-1.5 h-1.5 bg-gray-600 rounded-full mt-2"
            animate={{ y: [0, 16, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          />
        </div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
